import { z } from 'zod';

import type { DashboardMessageKey } from '../../../lib/messages';

export enum BlockReasonCreative {
  None = '',
  Other = 'OTHER',
  Policy = 'POLICY',
  AdQuality = 'ADQUALITY',
  Malware = 'MALWARE',
}

export enum BlockedListSortProperty {
  Creative = 'creative',
  CreatedOn = 'createdOn',
  UpdatedOn = 'updatedOn',
}

export function isBlockedListSortProperty(value: string): value is BlockedListSortProperty {
  return Object.values(BlockedListSortProperty).includes(value as BlockedListSortProperty);
}

export enum BlockedListSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export enum BlockedListFileFormat {
  Csv = 'csv',
  Excel = 'excel',
}

const entityIdSchema = z.union([z.string(), z.number()]).transform(String);
const blockedCreativePublisherSchema = z.looseObject({
  id: entityIdSchema,
  name: z.string(),
});
const blockedCreativeDomainSchema = z.looseObject({
  id: entityIdSchema,
  url: z.string(),
});

export const blockedCreativeSchema = z.looseObject({
  id: entityIdSchema,
  creative: z.string(),
  reason: z.enum(BlockReasonCreative),
  comment: z.string().nullish(),
  blockAll: z.boolean().default(false),
  active: z.boolean().default(true),
  createdOn: z.string().nullish(),
  updatedOn: z.string().nullish(),
  publishers: z
    .array(blockedCreativePublisherSchema)
    .nullish()
    .transform((value) => value ?? []),
  domains: z
    .array(blockedCreativeDomainSchema)
    .nullish()
    .transform((value) => value ?? []),
});

export const blockedCreativesPageSchema = z.looseObject({
  content: z.array(blockedCreativeSchema),
  totalElements: z.number(),
});

export const blockedListPublisherOptionSchema = z.looseObject({
  id: entityIdSchema,
  name: z.string(),
});

export const blockedListPublisherPageSchema = z.looseObject({
  content: z.array(blockedListPublisherOptionSchema),
  totalElements: z.number(),
});

export type BlockedCreative = z.infer<typeof blockedCreativeSchema>;
export type BlockedListPublisherOption = z.infer<typeof blockedListPublisherOptionSchema>;

export interface BlockedCreativeMutationPayload {
  creative: string;
  reason: BlockReasonCreative;
  comment: string;
  blockAll: boolean;
  active: boolean;
  publisherIds: string[];
  domainIds: string[];
}

export interface BlockedCreativesFilter {
  searchTerm: string;
  page: number;
  size: number;
  sortProperty: BlockedListSortProperty | null;
  sortDirection: BlockedListSortDirection;
}

export const blockedListPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultBlockedCreativesFilter: BlockedCreativesFilter = {
  searchTerm: '',
  page: 0,
  size: 25,
  sortProperty: BlockedListSortProperty.UpdatedOn,
  sortDirection: BlockedListSortDirection.Descending,
};

export function blockedReasonLabel(reason: BlockReasonCreative): DashboardMessageKey {
  const labels: Record<BlockReasonCreative, DashboardMessageKey> = {
    [BlockReasonCreative.None]: 'blockedList.reason.none',
    [BlockReasonCreative.Other]: 'blockedList.reason.other',
    [BlockReasonCreative.Policy]: 'blockedList.reason.policy',
    [BlockReasonCreative.AdQuality]: 'blockedList.reason.adQuality',
    [BlockReasonCreative.Malware]: 'blockedList.reason.malware',
  };
  return labels[reason];
}
